import type { ClassData, LibraryData } from '@/core/types/library.type'

import { getConstructorToken } from './getConstructorToken'
import { matchesAnyPattern, matchesPattern, parseFilterPatterns } from './matchFilterPattern'

export interface TypeFilterBreakdown {
  fqn: string
  label: string
  /** Methods + constructors remaining after the method filter. */
  methodCount: number
  /** Fields/properties — never affected by the method filter. */
  fieldCount: number
}

export interface FilterStatistics {
  typesExposed: number
  fieldsExposed: number
  methodsExposed: number
  perType: TypeFilterBreakdown[]
}

function classFqn(classData: ClassData): string {
  return `${classData.namespace}.${classData.name}`
}

/** Type filter: empty patterns include everything; otherwise match full name (or short name for PHP/Node). */
function typeMatches(classData: ClassData, typePatterns: string[]): boolean {
  if (typePatterns.length === 0) return true
  return matchesAnyPattern(classFqn(classData), typePatterns) || matchesAnyPattern(classData.name, typePatterns)
}

/**
 * Method filter for a single member.
 * - Empty patterns include everything.
 * - Bare pattern (no dot) matches the member name on every included type.
 * - Qualified pattern ({Type}.{member}) matches against the full name and the short name.
 */
function memberMatches(memberName: string, fqn: string, shortName: string, methodPatterns: string[]): boolean {
  if (methodPatterns.length === 0) return true

  return methodPatterns.some((pattern) => {
    if (pattern.includes('.')) {
      return matchesPattern(`${fqn}.${memberName}`, pattern) || matchesPattern(`${shortName}.${memberName}`, pattern)
    }
    return matchesPattern(memberName, pattern)
  })
}

/**
 * Compute hosted-library statistics for a given (types, methods) filter pair, mirroring
 * the GG/GMA contract used by `getHostedLibraryStatisticsLocal`.
 *
 * Type filter is applied first; the method filter then trims members on the surviving types.
 * Types are retained even when every method is filtered out (zero-method types stay visible).
 */
export function computeFilterStatistics(libraryData: LibraryData, types: string, methods: string): FilterStatistics {
  const typePatterns = parseFilterPatterns(types)
  const methodPatterns = parseFilterPatterns(methods)
  const constructorToken = getConstructorToken(libraryData.runtimeName)

  const classes = Object.values(libraryData.classes ?? {})
  const includedTypes = classes.filter((classData) => typeMatches(classData, typePatterns))

  const perType: TypeFilterBreakdown[] = includedTypes.map((classData) => {
    const fqn = classFqn(classData)
    const methodNames = Object.values(classData.methods ?? {}).map((method) => method.name)
    const constructorCount = Object.keys(classData.constructors ?? {}).length
    const fieldCount = Object.keys(classData.fields ?? {}).length

    let methodCount = methodNames.filter((name) => memberMatches(name, fqn, classData.name, methodPatterns)).length

    if (constructorCount > 0 && memberMatches(constructorToken, fqn, classData.name, methodPatterns)) {
      methodCount += constructorCount
    }

    return { fqn, label: classData.name, methodCount, fieldCount }
  })

  return {
    typesExposed: perType.length,
    fieldsExposed: perType.reduce((sum, type) => sum + type.fieldCount, 0),
    methodsExposed: perType.reduce((sum, type) => sum + type.methodCount, 0),
    perType,
  }
}
